/* eslint-disable no-unsafe-optional-chaining */
import Decimal from 'decimal.js';
import { camelToSnakeCase, getSortedData } from '../../../RevisaoPrecos/utils';
import { NegotitationEditDatapointProps, NegotitationRowDataProps, SuppliersResponseContentProps } from './NegociacaoFornecedor.types';
import { CostNegotiationMode, DEFAULT_COST_NEGOTIATION_MODE } from './costNegotiation';

export const formatDecimal = (value: number, decimals: number) => {
  if (value == null || !Number.isFinite(value)) return '0,00';
  return new Decimal(value).toDecimalPlaces(decimals).toFixed(decimals).toString().replace('.', ',');
};

/** Diz se algum insumo do cálculo não veio. */
const hasMissingInput = (...values: Array<number | null | undefined>) => values.some(value => value === null || value === undefined);

export const newPriceCalc = (data: SuppliersResponseContentProps, value: number) => {
  if (hasMissingInput(data.salesTaxPercentage, data.expensesPercent)) return null;

  const totalPerc = new Decimal(data.newObjectiveMargin ?? 0).plus(data.salesTaxPercentage).plus(data.expensesPercent);

  const denom = new Decimal(100).minus(totalPerc);
  if (denom.lte(0) || !denom.isFinite()) return null;

  return new Decimal(value).dividedBy(denom).times(100).toDecimalPlaces(2).toNumber();
};

export const newCostCalc = (data: SuppliersResponseContentProps, margin: number, newPrice: number) => {
  if (hasMissingInput(margin, data.salesTaxPercentage, data.expensesPercent)) return 0;

  const totalPerc = new Decimal(margin).plus(data.salesTaxPercentage).plus(data.expensesPercent);

  return new Decimal(newPrice).times(new Decimal(100).minus(totalPerc)).dividedBy(100).toDecimalPlaces(2).toNumber();
};

export const PMZNewCostCalc = (data: SuppliersResponseContentProps, value: number) => {
  if (hasMissingInput(value, data.salesTaxPercentage, data.expensesPercent)) return 0;

  const totalPerc = new Decimal(data.salesTaxPercentage).plus(data.expensesPercent);

  return new Decimal(value).dividedBy(new Decimal(100).minus(totalPerc)).times(100).toDecimalPlaces(2, Decimal.ROUND_CEIL).toNumber();
};

export const priceMarginCalc = (data: SuppliersResponseContentProps, price: number, cost: number) => {
  if (!Number.isFinite(price) || price <= 0) return null;
  if (hasMissingInput(cost, data.salesTaxPercentage, data.expensesPercent)) return null;

  const priceDec = new Decimal(price);
  const costDec = new Decimal(cost);
  const taxPerc = new Decimal(data.salesTaxPercentage).plus(data.expensesPercent);

  const tax = priceDec.times(taxPerc).dividedBy(100);
  const liquidPrice = priceDec.minus(tax);

  return liquidPrice.minus(costDec).dividedBy(priceDec).times(100).toDecimalPlaces(2).toNumber();
};

export const priceMarginCalcWithNewPMZ = (price: number, newPMZ: number) => {
  const priceDecimal = new Decimal(price).toDecimalPlaces(2);
  if (priceDecimal.isZero()) return null;
  const newPMZDecimal = new Decimal(newPMZ).toDecimalPlaces(2);
  return priceDecimal.minus(newPMZDecimal).dividedBy(priceDecimal).times(100).toNumber();
};

export const newPriceCompetitorsCalc = (data: SuppliersResponseContentProps, newPrice: number) => {
  const competitorsPrice = data.competitorsPrice;
  if (competitorsPrice === null || competitorsPrice === 0) return null;
  const competitorsPriceDecimal = new Decimal(competitorsPrice).toDecimalPlaces(2);
  if (competitorsPriceDecimal.isZero()) return null;
  const newPriceDecimal = new Decimal(newPrice).toDecimalPlaces(2);
  return newPriceDecimal.dividedBy(competitorsPriceDecimal).times(100).toNumber();
};

export type TargetMarginInputs = {
  newObjectiveMargin?: number | null;
  objectiveMargin?: number | null;
  marginRetailPrice?: number | null;
};

/**
 * Margem-alvo do preço ideal de compra: a editada pelo comprador, senão a objetiva cadastrada, senão a
 * margem atual da linha quando positiva. Editada em zero é dado e vence o recuo. Sem nenhuma das três,
 * não há margem-alvo e o preço ideal fica sem valor.
 */
export const resolveTargetMargin = ({ newObjectiveMargin, objectiveMargin, marginRetailPrice }: TargetMarginInputs): number | null => {
  if (newObjectiveMargin !== null && newObjectiveMargin !== undefined) return newObjectiveMargin;
  if (objectiveMargin !== null && objectiveMargin !== undefined) return objectiveMargin;
  if (marginRetailPrice !== null && marginRetailPrice !== undefined && marginRetailPrice > 0) return marginRetailPrice;
  return null;
};

const percentOrZero = (value?: number | null) => new Decimal(value ?? 0);

const purchaseTaxFactorOf = (data: SuppliersResponseContentProps) => new Decimal(1).plus(percentOrZero(data.purchaseTaxPercentage).dividedBy(100));

/** Concorrente se positivo, senão o preço vigente se positivo; sem os dois não há base. */
const idealPriceBaseOf = (data: SuppliersResponseContentProps): number | null => {
  if (data.competitorsPrice !== null && data.competitorsPrice !== undefined && data.competitorsPrice > 0) return data.competitorsPrice;
  if (data.retailPrice !== null && data.retailPrice !== undefined && data.retailPrice > 0) return data.retailPrice;
  return null;
};

/**
 * Preço ideal de compra: o teto que preserva a margem-alvo praticando o preço de referência. Imposto de
 * venda ou despesa ausente vale zero. Trunca no centavo. `null` é sem valor: sem base, sem margem-alvo,
 * ou margem-alvo, imposto e despesa somando 100% ou mais.
 */
export const bestIdealPriceCalc = (
  data: SuppliersResponseContentProps,
  targetMargin: number | null | undefined,
  costNegotiationMode = DEFAULT_COST_NEGOTIATION_MODE,
): number | null => {
  const basePrice = idealPriceBaseOf(data);
  if (basePrice === null) return null;
  if (targetMargin === null || targetMargin === undefined || !Number.isFinite(targetMargin)) return null;

  const remainingPercent = new Decimal(100).minus(targetMargin).minus(percentOrZero(data.expensesPercent)).minus(percentOrZero(data.salesTaxPercentage));
  if (remainingPercent.lte(0)) return null;

  const liquidCost = new Decimal(basePrice).times(remainingPercent).dividedBy(100);

  if (costNegotiationMode === CostNegotiationMode.ENTRY_COST) {
    const purchaseTaxFactor = purchaseTaxFactorOf(data);
    if (purchaseTaxFactor.lte(0)) return null;

    return liquidCost.dividedBy(purchaseTaxFactor).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();
  }

  return liquidCost.toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();
};

/**
 * PMZ do preço ideal de compra: o preço de venda de margem zero com o preço ideal como custo. Imposto
 * de venda ou despesa ausente vale zero. Arredonda para cima no centavo. Sem preço ideal, sem valor.
 */
export const bestIdealPricePMZCalc = (
  data: SuppliersResponseContentProps,
  price: number | null | undefined,
  costNegotiationMode = DEFAULT_COST_NEGOTIATION_MODE,
): number | null => {
  if (price === null || price === undefined || !Number.isFinite(price)) return null;

  const remainingPercent = new Decimal(100).minus(percentOrZero(data.expensesPercent)).minus(percentOrZero(data.salesTaxPercentage));
  if (remainingPercent.lte(0)) return null;

  const liquidCost = costNegotiationMode === CostNegotiationMode.ENTRY_COST ? new Decimal(price).times(purchaseTaxFactorOf(data)) : new Decimal(price);

  return liquidCost.dividedBy(remainingPercent).times(100).toDecimalPlaces(2, Decimal.ROUND_UP).toNumber();
};

/** Preço ideal de compra e o PMZ dele, com a margem-alvo resolvida da própria linha. */
export const idealPurchasePriceOf = (data: SuppliersResponseContentProps & TargetMarginInputs, costNegotiationMode = DEFAULT_COST_NEGOTIATION_MODE) => {
  const bestIdealPrice = bestIdealPriceCalc(data, resolveTargetMargin(data), costNegotiationMode);
  const bestIdealPricePMZ = bestIdealPricePMZCalc(data, bestIdealPrice, costNegotiationMode);
  return { bestIdealPrice, bestIdealPricePMZ };
};

export const calculateVariationPercentage = (cost: number, newCost: number) => {
  const costDecimal = new Decimal(cost).toDecimalPlaces(2);
  if (costDecimal.isZero()) return 0;
  return new Decimal(newCost).minus(costDecimal).dividedBy(costDecimal).times(100).toNumber();
};

export const calculateVariation = (newNumber: number, originalNumber: number) => new Decimal(newNumber).minus(new Decimal(originalNumber)).toNumber();

export const competitivenessIndicator = (value: string) => {
  const indicator = {
    RULE_CPI_MAX: '>',
    RULE_CPI_MIN: '<',
    RULE_CPI_AVG: 'x̅',
  } as const;
  return (indicator as Record<string, string>)[value] ?? '';
};

type SortMode = 'snake' | 'camel';

export const getFiltrosModel = ({ size = 20, sort, page = 0, ...filter }: Record<string, any>, sortMode: SortMode = 'snake') => {
  const CURRENT_SORT = {
    snake: camelToSnakeCase(getSortedData({ sort }) ?? ''),
    camel: getSortedData({ sort }) ?? '',
  } as const;
  const filterWithoutResponsible = Object.keys(filter).reduce<Record<string, any>>((result, key) => {
    if (key !== 'responsible') result[key] = filter[key];
    return result;
  }, {});

  return {
    page: page <= 0 ? 0 : page - 1,
    sort: CURRENT_SORT[sortMode],
    size,
    ...filterWithoutResponsible,
  };
};

export const getEditedDatapointModel = (
  rowData: NegotitationRowDataProps,
  // A verba nao mora na linha: ela vive no estado de edicao da grade, para que desfazer saiba
  // para onde voltar. Chega aqui por fora, e ausente quer dizer "o comprador nao mexeu nela".
  verba?: Pick<NegotitationEditDatapointProps, 'sellinAllowance' | 'sellinAllowanceOrigin'> | null,
): NegotitationEditDatapointProps => ({
  id: rowData.productsToBePricedId,
  ...(verba ?? {}),
  newObjectiveMargin: rowData.newObjectiveMargin,
  newCost: rowData.newCost,
  newPrice: rowData.newPrice,
  newPriceMargin: rowData.newPriceMargin,
  newPriceCompetitors: rowData.newPriceCompetitors,
  bestIdealPrice: rowData.bestIdealPrice,
  bestIdealPricePMZ: rowData.bestIdealPricePMZ,
  PMZNewCost: rowData.PMZNewCost,
  costIndicator: rowData.costIndicator,
  visionSaved: rowData.visionSaved,
  cluster: rowData.cluster,
  negotiationSupplierDatapointStatus: rowData.negotiationSupplierDatapointStatus,
});
